import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI } from '@/services/api';
import { Briefcase, Users, BarChart3, Search, Plus, ClipboardList, AlertTriangle, Mail, Building2 } from 'lucide-react';

export default function RecruiterHome() {
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [postingStatus, setPostingStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try { const p = await recruiterAPI.getProfile(); setProfile(p); setHasProfile(true); } catch { setHasProfile(false); }
      try { setAnalytics(await recruiterAPI.getAnalytics()); } catch { /* ignore */ }
      try { setPostingStatus(await recruiterAPI.getPostingStatus()); } catch { /* ignore */ }
    };
    load();
    const onJayUpdate = () => load();
    window.addEventListener('jay:profile-updated', onJayUpdate);
    return () => window.removeEventListener('jay:profile-updated', onJayUpdate);
  }, []);

  if (hasProfile === false) {
    return (
      <div data-testid="recruiter-setup-prompt">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">Welcome to Numuv</h1>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-6">
          <p className="text-amber-700 dark:text-amber-300 mb-3">Please set up your recruiter profile to get started.</p>
          <button onClick={() => navigate('/recruiter/profile-setup')} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm" data-testid="setup-profile-btn">Set Up Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="recruiter-home">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Recruiter Dashboard</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">{profile?.organization_name || 'Your organization'}</p>
        </div>
        <button onClick={() => navigate('/recruiter/jobs')} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm hover:bg-slate-800 dark:hover:bg-zinc-200" data-testid="post-job-btn"
          disabled={postingStatus && !postingStatus.can_post}>
          <Plus className="w-4 h-4" /> Post a Job
        </button>
      </div>

      {/* Posting eligibility banner */}
      {postingStatus && !postingStatus.can_post && (
        <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4" data-testid="posting-status-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-700 dark:text-amber-300 font-medium text-sm mb-2">Job posting is currently restricted</p>
              <div className="space-y-1.5">
                {!postingStatus.org_approved && (
                  <div className="flex items-center gap-2 text-amber-400/80 text-xs">
                    <Building2 className="w-3.5 h-3.5" />
                    <span>Your organization ({postingStatus.org_name || 'N/A'}) is pending admin approval</span>
                  </div>
                )}
                {!postingStatus.email_verified && (
                  <div className="flex items-center gap-2 text-amber-400/80 text-xs">
                    <Mail className="w-3.5 h-3.5" />
                    <span>Please verify your email address — go to <button onClick={() => navigate('/recruiter/settings')} className="underline hover:text-amber-700 dark:hover:text-amber-300">Settings</button></span>
                  </div>
                )}
              </div>
              <p className="text-slate-500 dark:text-zinc-500 text-xs mt-2">You can still set up your profile, browse candidates, and configure your team while waiting.</p>
            </div>
          </div>
        </div>
      )}

      {/* Clickable stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <ActionCard icon={Briefcase} label="Total Jobs" value={analytics?.total_jobs || 0} color="blue" onClick={() => navigate('/recruiter/jobs')} hint="Manage jobs" />
        <ActionCard icon={Briefcase} label="Active Jobs" value={analytics?.active_jobs || 0} color="emerald" onClick={() => navigate('/recruiter/jobs')} hint="View active" />
        <ActionCard icon={Users} label="Total Matches" value={analytics?.total_matches || 0} color="purple" onClick={() => navigate('/recruiter/jobs')} hint="View candidates" />
        <ActionCard icon={ClipboardList} label="Screenings Done" value={analytics?.screening_completed || 0} color="amber" onClick={() => navigate('/recruiter/jobs')} hint="Open in Jobs" />
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-8">
        <QuickBtn icon={Search} label="AI Search" onClick={() => navigate('/recruiter/search')} />
        <QuickBtn icon={Briefcase} label="My Jobs" onClick={() => navigate('/recruiter/jobs')} />
        <QuickBtn icon={BarChart3} label="Analytics" onClick={() => navigate('/recruiter/analytics')} />
        <QuickBtn icon={Users} label="Team" onClick={() => navigate('/recruiter/team')} />
      </div>

      {/* Recent Jobs — clickable */}
      {analytics?.recent_jobs?.length > 0 && (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5">
          <h3 className="text-slate-900 dark:text-white font-medium mb-4">Recent Jobs</h3>
          <div className="space-y-2">
            {analytics.recent_jobs.map(j => (
              <div key={j.id} className="flex items-center justify-between py-2.5 border-b border-slate-200 dark:border-zinc-800/30 last:border-0 hover:bg-slate-100 dark:hover:bg-zinc-800/20 rounded px-2 -mx-2 cursor-pointer transition-colors"
                onClick={() => navigate(`/recruiter/jobs/${j.id}`)} data-testid={`recent-job-${j.id}`}>
                <div>
                  <p className="text-slate-600 dark:text-zinc-300 text-sm">{j.title}</p>
                  <p className="text-slate-500 dark:text-zinc-600 text-xs">{new Date(j.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 dark:text-zinc-400 text-xs">{j.matches} matches</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${j.status === 'active' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-500'}`}>{j.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, label, value, color, onClick, hint }) {
  const colors = { emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10', blue: 'text-blue-600 dark:text-blue-400 bg-blue-500/10', amber: 'text-amber-600 dark:text-amber-400 bg-amber-500/10', purple: 'text-purple-600 dark:text-purple-400 bg-purple-500/10' };
  return (
    <button onClick={onClick} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 text-left hover:border-slate-300 dark:hover:border-zinc-700/50 transition-all group" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${colors[color]}`}><Icon className="w-4 h-4" /></div>
        <div><p className="text-slate-500 dark:text-zinc-500 text-xs">{label}</p><p className="text-slate-900 dark:text-white font-semibold text-lg">{value}</p></div>
      </div>
      <p className="text-slate-500 dark:text-zinc-600 text-[10px] mt-2 group-hover:text-slate-500 dark:group-hover:text-zinc-400 transition-colors">{hint} &rarr;</p>
    </button>
  );
}

function QuickBtn({ icon: Icon, label, onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-zinc-800/50 border border-slate-200 dark:border-zinc-800 rounded-lg text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-zinc-600 transition-colors" data-testid={`quick-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
